import React from 'react';
import { Row, Select } from 'antd';
import PropTypes from 'prop-types';
import { Common } from 'ontraccr-common';

import OnTraccrTextInput from '../../../../common/inputs/OnTraccrTextInput';

import FieldTriggerCheckbox from '../FieldTriggerCheckbox';
import ConditionalRenderingRow from '../ConditionalRenderingRow';
import OptionalRow from '../OptionalRow';
import HideIfEmptyInPDFCheckbox from '../HideIfEmptyInPDFCheckbox';
import { getDynamicAttributeLinkOptions } from '../Attribute.helpers';

import { getAttributeTypes, INVALID_TYPES } from './dynamicAttributes.constants';

function DynamicAttributeConfigure({
  t,
  id,
  setConfigProps,
  configProps = {},
  setCanSubmit,
  sections = [],
  isExternalForm,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  contactAddressBooks = {},
  labels = [],
  buckets = [],
  scheduleCategories = [],
  ccCustomFields,
  userCustomFields,
  projectCustomFields,
  fullEquipmentTypes = [],
  customerCustomFields,
  customTables = {},
  isV3Form = false,
  isMultiDivisionForm = false,
} = {}) {
  const attributeOptions = getAttributeTypes(t, { isMultiDivisionForm });
  const {
    optional,
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    dataType,
    subType,
    linkField,
  } = configProps;

  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      title: value,
    });
    setCanSubmit(value);
  };

  const setConditionalRenderingFormula = (newVal) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: newVal,
    });
  };

  const updateCheckbox = (key) => (checked) => {
    setConfigProps({
      ...configProps,
      [key]: checked,
    });
  };

  const setDataType = (newDataType) => {
    const typeChanged = newDataType !== configProps.dataType;
    if (!typeChanged) return;

    const newLinkOptions = getDynamicAttributeLinkOptions({
      dataType: newDataType,
      sections,
    });

    // Auto-populate if exactly one option, otherwise clear
    const newLinkField = newLinkOptions.length === 1 ? newLinkOptions[0].value : undefined;

    setConfigProps({
      ...configProps,
      dataType: newDataType,
      subType: undefined,
      fieldId: undefined,
      linkField: newLinkField,
    });
  };

  const setLinkField = (value) => {
    setConfigProps({
      ...configProps,
      linkField: value,
    });
  };

  const setSubDataType = (newSubType) => {
    const typeChanged = newSubType !== configProps.subType;
    if (!typeChanged) return;
    setConfigProps({
      ...configProps,
      subType: newSubType,
      fieldId: undefined,
    });
  };

  let fields = [];
  let fieldMap = {};
  switch (dataType) {
    case 'projects':
      fields = projectCustomFields?.fields ?? [];
      break;
    case 'costcodes':
      fields = ccCustomFields?.fields ?? [];
      break;
    case 'users':
      fields = userCustomFields?.fields ?? [];
      break;
    case 'equipment': {
      const ourType = fullEquipmentTypes.find((type) => (
        type.id === subType
        || (subType === null && type.isDefault)
      ));
      fields = ourType?.customFields?.fields ?? [];
      break;
    }
    case 'customers':
      fields = customerCustomFields?.fields ?? [];
      break;
    default:
      fields = [];
  }

  const fieldOpts = fields
    .filter((field) => {
      if (field.selectedType === 'table') {
        return field.configProps?.dataType in customTables;
      }
      return !INVALID_TYPES.has(field.selectedType);
    })
    .map((field) => ({
      value: field.id,
      label: field.title,
    }));

  fieldMap = Common.getIdMap(fields);

  const setFieldId = (fieldId) => {
    const typeChanged = fieldId !== configProps.fieldId;
    if (!typeChanged) return;
    const targetField = fieldMap[fieldId];
    setConfigProps({
      ...configProps,
      fieldId,
      fieldType: targetField?.type,
      fieldDataType: targetField?.configProps?.dataType,
    });
  };

  const linkOptions = getDynamicAttributeLinkOptions({
    dataType,
    sections,
  });

  return (
    <div>
      <Row className="form-required-field">
        Title:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <OptionalRow onChange={updateCheckbox('optional')} optional={optional} disabled={disableOptional} />
      <ConditionalRenderingRow
        id={id}
        sections={sections}
        customers={customers}
        projects={projects}
        users={users}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
        onChange={updateCheckbox('hasConditionalRendering')}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      <Row className="form-required-field" style={{ marginTop: 10 }} >
        Type:
      </Row>
      <Row style={{ marginTop: 10 }} gutter={10}>
        <Select
          style={{ width: '100%', margin: '0px 10px' }}
          placeholder="Select attribute type"
          onChange={setDataType}
          value={configProps.dataType}
          optionFilterProp="label"
          options={attributeOptions}
        />
      </Row>
      {
        configProps.dataType === 'equipment'
        && (
          <Row style={{ marginTop: 10 }} gutter={10}>
            <Select
              style={{ width: '100%', margin: '0px 10px' }}
              placeholder="Select Equipment Type"
              onChange={setSubDataType}
              value={configProps.subType}
              optionFilterProp="label"
              options={
                fullEquipmentTypes.map((type) => ({
                  value: type.isDefault ? null : type.id,
                  label: type.name,
                }))
              }
            />
          </Row>
        )
      }
      <Row className="form-required-field" style={{ marginTop: 10 }}>
        Field:
      </Row>
      <Row style={{ marginTop: 10 }} gutter={10}>
        <Select
          style={{ width: '100%', margin: '0px 10px' }}
          placeholder="Select field"
          onChange={setFieldId}
          value={configProps.fieldId}
          optionFilterProp="label"
          options={fieldOpts}
        />
      </Row>
      {dataType && linkOptions.length > 0 && (
        <Row style={{ marginTop: 10 }} gutter={10}>
          <Select
            style={{ width: '100%', margin: '0px 10px' }}
            placeholder="Link to field"
            onChange={setLinkField}
            value={linkField}
            optionFilterProp="label"
            options={linkOptions}
            allowClear
          />
        </Row>
      )}
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={updateCheckbox('fieldTrigger')}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
    </div>
  );
}
/* eslint-disable react/forbid-prop-types */
DynamicAttributeConfigure.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  setConfigProps: PropTypes.func.isRequired,
  configProps: PropTypes.object,
  setCanSubmit: PropTypes.func.isRequired,
  sections: PropTypes.array,
  isExternalForm: PropTypes.bool,
  templateId: PropTypes.string.isRequired,
  divisions: PropTypes.array.isRequired,
  projectId: PropTypes.string,
  name: PropTypes.string,
  isBoardCards: PropTypes.bool,
  setFieldTriggerEditable: PropTypes.func.isRequired,
  disableOptional: PropTypes.bool,
  customers: PropTypes.array,
  projects: PropTypes.array,
  users: PropTypes.array,
  costcodes: PropTypes.array,
  phases: PropTypes.array,
  projectIdMap: PropTypes.object,
  vendors: PropTypes.array,
  equipment: PropTypes.array,
  formTemplates: PropTypes.array,
  contactAddressBooks: PropTypes.object,
  labels: PropTypes.array,
  buckets: PropTypes.array,
  ccCustomFields: PropTypes.array,
  userCustomFields: PropTypes.array,
  projectCustomFields: PropTypes.array,
  customerCustomFields: PropTypes.array,
  fullEquipmentTypes: PropTypes.array,
  customTables: PropTypes.object,
  isV3Form: PropTypes.bool,
  isMultiDivisionForm: PropTypes.bool,
};

DynamicAttributeConfigure.defaultProps = {
  configProps: {},
  sections: [],
  isExternalForm: false,
  projectId: null,
  name: '',
  isBoardCards: false,
  disableOptional: false,
  customers: [],
  projects: [],
  users: [],
  costcodes: [],
  phases: [],
  projectIdMap: {},
  vendors: [],
  equipment: [],
  formTemplates: [],
  contactAddressBooks: {},
  labels: [],
  buckets: [],
  ccCustomFields: [],
  userCustomFields: [],
  projectCustomFields: [],
  fullEquipmentTypes: [],
  customerCustomFields: [],
  customTables: {},
  isV3Form: false,
  isMultiDivisionForm: false,
};

export default DynamicAttributeConfigure;
